import type { Geometry } from 'geojson';

import { fetchGeometry, fetchReverse } from './geocoder';
import type { GeocodeResult, HierarchyNode } from './types';
import { isPointInGeometry } from '../geo';

/** Что оказалось под курсором в момент клика (по векторным тайлам). */
export type MapHit =
  | { readonly kind: 'building' }
  /** name - подпись улицы из тайлов (например "Невский проспект") */
  | { readonly kind: 'street'; readonly name: string }
  | { readonly kind: 'other' }
  /** Подъезд (слой entrance в тайлах): номер, квартиры и центр дома, к
   *  которому он относится, - адрес ищется по дому, а не по точке на стене. */
  | {
      readonly kind: 'entrance';
      readonly ref: string | null;
      readonly flats: string | null;
      readonly house: readonly [number, number] | null;
    };

/** Описание места для всплывающей подсказки и точки маршрута. */
export interface Place {
  readonly title: string;
  readonly subtitle: string;
  /** Точка маршрута: у дома - координаты дома, иначе - место клика. */
  readonly endpoint: GeocodeResult;
  /** Где поставить маркер и подсказку, [lat, lon]. */
  readonly marker: [number, number];
  /** Подпись в поле маршрута, если title без контекста непонятен ("Подъезд 2"). */
  readonly routeLabel?: string;
}

// уровни ГАР: 1-7 - регион...элемент планировочной структуры, 8 - улица, 10 - дом
const STREET_LEVEL = 8;
const HOUSE_LEVEL = 10;

const nodeLabel = (node: HierarchyNode): string =>
  `${node.short === 'д' ? 'д.' : node.short} ${node.name}`.trim();

/** "Владимирский округ, Санкт-Петербург" - снизу вверх, как в подсказках поиска. */
const ancestorsLabel = (nodes: readonly HierarchyNode[]): string =>
  nodes
    .slice()
    .reverse()
    .map(node => node.name)
    .join(', ');

const fullLabel = (nodes: readonly HierarchyNode[], tail?: string): string =>
  [...nodes.map(nodeLabel), ...(tail ? [tail] : [])].join(', ');

const words = (text: string): string[] =>
  text
    .toLowerCase()
    .replace(/ё/g, 'е')
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);

/** Подпись из тайлов ("набережная реки Фонтанки") содержит все слова
 *  названия из ГАР ("Реки Фонтанки"). */
function isSameStreet(tileName: string, node: HierarchyNode): boolean {
  const tileWords = new Set(words(tileName));
  const nodeWords = words(node.name);
  return nodeWords.length > 0 && nodeWords.every(word => tileWords.has(word));
}

export function pointEndpoint(
  lat: number,
  lon: number,
  fullAddress: string,
  hierarchy: readonly HierarchyNode[] = [],
): GeocodeResult {
  return {
    id: `point:${lat},${lon}`,
    fullAddress,
    hierarchy,
    components: {},
    latitude: lat,
    longitude: lon,
    score: 0,
    precision: 'unknown',
    source: 'reverse',
  };
}

const formatCoords = (lat: number, lon: number): string => `${lat.toFixed(5)}, ${lon.toFixed(5)}`;

/** Место без адреса - геокодер ничего не нашёл или упал. */
export function barePlace(lat: number, lon: number): Place {
  const coords = formatCoords(lat, lon);
  return {
    title: 'Точка на карте',
    subtitle: coords,
    endpoint: pointEndpoint(lat, lon, coords),
    marker: [lat, lon],
  };
}

const geometryOrNull = (aoId: number, signal?: AbortSignal): Promise<Geometry | null> =>
  fetchGeometry(aoId, signal)
    .then(feature => feature?.geometry ?? null)
    .catch((error: unknown) => {
      if ((error as Error).name === 'AbortError') throw error;
      console.error('Geometry fetch failed:', error);
      return null;
    });

/**
 * Что показать по клику в точке (как в Яндекс.Картах): здание - адрес дома
 * из обратного геокодирования; улица - сама улица (её подпись из тайлов,
 * сверенная с иерархией ГАР ближайшего дома); иначе - самая мелкая
 * территория из иерархии (город, посёлок, район, регион), в контур которой
 * попала точка.
 */
export async function resolvePlace(
  lat: number,
  lon: number,
  hit: MapHit,
  signal?: AbortSignal,
): Promise<Place> {
  if (hit.kind === 'entrance') return resolveEntrance(lat, lon, hit, signal);

  // /reverse всегда начинает с ближайшего дома - даже для клика по улице или
  // реке его иерархия даёт улицу, район и город вокруг точки
  const result = await fetchReverse(lat, lon, signal);
  if (!result) {
    return hit.kind === 'street'
      ? { ...barePlace(lat, lon), title: hit.name, endpoint: pointEndpoint(lat, lon, hit.name) }
      : barePlace(lat, lon);
  }

  const { hierarchy } = result;

  if (hit.kind === 'building' && result.precision === 'house' && hierarchy.length > 0) {
    const primaryCount = hierarchy.length > 1 ? 2 : 1;
    return {
      title: hierarchy.slice(-primaryCount).map(nodeLabel).join(', '),
      subtitle: ancestorsLabel(hierarchy.slice(0, -primaryCount)),
      // The clicked building's interior point is the chosen location. An
      // address point can be displaced or lie on a neighbouring footprint.
      endpoint: { ...result, latitude: lat, longitude: lon },
      marker: [lat, lon],
    };
  }

  if (hit.kind === 'street') {
    const streetIndex = hierarchy.findLastIndex(
      node => node.level < HOUSE_LEVEL && isSameStreet(hit.name, node),
    );
    if (streetIndex !== -1) {
      const path = hierarchy.slice(0, streetIndex + 1);
      return {
        title: hit.name,
        subtitle: ancestorsLabel(path.slice(0, -1)),
        endpoint: pointEndpoint(lat, lon, fullLabel(path), path),
        marker: [lat, lon],
      };
    }
    // ближайший дом стоит на другой улице (перекрёсток, промзона) - улицу
    // берём из тайлов, а район и город - из иерархии этого дома
    const ancestors = hierarchy.filter(node => node.level < STREET_LEVEL);
    return {
      title: hit.name,
      subtitle: ancestorsLabel(ancestors),
      endpoint: pointEndpoint(lat, lon, fullLabel(ancestors, hit.name), ancestors),
      marker: [lat, lon],
    };
  }

  // территория: от самой мелкой к крупной, первая, в чей контур попала точка
  // (ближайший дом может стоять в соседней деревне, а клик - в поле между ними)
  const areas = hierarchy.filter(node => node.level < STREET_LEVEL);
  const geometries = await Promise.all(areas.map(node => geometryOrNull(node.ao_id, signal)));
  for (let i = areas.length - 1; i >= 0; i--) {
    const geometry = geometries[i];
    if (!geometry || !isPointInGeometry([lon, lat], geometry)) continue;
    const path = areas.slice(0, i + 1);
    return {
      title: nodeLabel(areas[i]),
      subtitle: ancestorsLabel(path.slice(0, -1)) || formatCoords(lat, lon),
      endpoint: pointEndpoint(lat, lon, fullLabel(path), path),
      marker: [lat, lon],
    };
  }

  return barePlace(lat, lon);
}

/** Подъезд: "Подъезд 3", квартиры и адрес дома; точка маршрута - сам подъезд. */
async function resolveEntrance(
  lat: number,
  lon: number,
  hit: Extract<MapHit, { kind: 'entrance' }>,
  signal?: AbortSignal,
): Promise<Place> {
  const [houseLat, houseLon] = hit.house ?? [lat, lon];
  const result = await fetchReverse(houseLat, houseLon, signal);
  const hierarchy = result?.precision === 'house' ? result.hierarchy : [];
  const title = hit.ref ? `Подъезд ${hit.ref}` : 'Вход';
  const flats = hit.flats ? `кв. ${hit.flats.replace(/-/g, '–')}` : '';
  const house = hierarchy.length > 1 ? hierarchy.slice(-2).map(nodeLabel).join(', ') : '';
  const address = hierarchy.length ? fullLabel(hierarchy) : formatCoords(lat, lon);
  return {
    title,
    routeLabel: house ? `${house}, ${title.toLowerCase()}` : title,
    subtitle: [flats, house].filter(Boolean).join(' · ') || formatCoords(lat, lon),
    endpoint: pointEndpoint(lat, lon, `${address}, ${title.toLowerCase()}`, hierarchy),
    marker: [lat, lon],
  };
}
